import axios from 'axios';
import {
  ChatConversationSummary,
  ChatModelsResponse,
  ChatRequest,
  ChatResponse,
  ChatStreamEvent,
  ChatStreamHandlers,
  ChatSummaryRequest,
  ChatSummaryUrlResponse,
  ChatUploadSummary,
  ChatUserInstructionsResponse,
  GetChatConversationResponse,
  ListChatConversationsResponse,
  ListChatUploadsResponse,
} from '../interfaces/ChatInterface';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getChatModels = async (eoaId: string, accessToken: string): Promise<ChatModelsResponse> => {
  const response = await axios.get<ChatModelsResponse>(`${BASE_URL}/eoa/${eoaId}/chat/models`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
};

const getChatInstructions = async (eoaId: string, accessToken: string): Promise<string> => {
  const response = await axios.get<ChatUserInstructionsResponse>(`${BASE_URL}/eoa/${eoaId}/chat/instructions`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data.instructions;
};

const updateChatInstructions = async (eoaId: string, accessToken: string, instructions: string): Promise<string> => {
  const response = await axios.put<ChatUserInstructionsResponse>(
    `${BASE_URL}/eoa/${eoaId}/chat/instructions`,
    { instructions },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  return response.data.instructions;
};

const listChatUploads = async (eoaId: string, accessToken: string): Promise<ChatUploadSummary[]> => {
  const response = await axios.get<ListChatUploadsResponse>(`${BASE_URL}/eoa/${eoaId}/chat/uploads`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data.uploads;
};

const uploadChatUpload = async (eoaId: string, accessToken: string, file: File): Promise<ChatUploadSummary> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post<ChatUploadSummary>(`${BASE_URL}/eoa/${eoaId}/chat/uploads`, formData, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
};

const deleteChatUpload = async (eoaId: string, accessToken: string, uploadId: string): Promise<void> => {
  await axios.delete(`${BASE_URL}/eoa/${eoaId}/chat/uploads/${uploadId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

const listConversationUploads = async (
  eoaId: string,
  accessToken: string,
  scope: string
): Promise<ChatUploadSummary[]> => {
  const response = await axios.get<ListChatUploadsResponse>(`${BASE_URL}/eoa/${eoaId}/chat/conversation-uploads`, {
    params: { scope },
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data.uploads;
};

const uploadConversationUpload = async (
  eoaId: string,
  accessToken: string,
  scope: string,
  file: File
): Promise<ChatUploadSummary> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post<ChatUploadSummary>(`${BASE_URL}/eoa/${eoaId}/chat/conversation-uploads`, formData, {
    params: { scope },
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
};

const deleteConversationUpload = async (eoaId: string, accessToken: string, uploadId: string): Promise<void> => {
  await axios.delete(`${BASE_URL}/eoa/${eoaId}/chat/conversation-uploads/${uploadId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

const listChatConversations = async (
  eoaId: string,
  accessToken: string,
  scopePrefix?: string
): Promise<ChatConversationSummary[]> => {
  const response = await axios.get<ListChatConversationsResponse>(`${BASE_URL}/eoa/${eoaId}/chat/conversations/list`, {
    params: scopePrefix ? { scope_prefix: scopePrefix } : undefined,
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data.conversations;
};

const getChatConversation = async (
  eoaId: string,
  scope: string,
  accessToken: string
): Promise<GetChatConversationResponse> => {
  const response = await axios.get<GetChatConversationResponse>(`${BASE_URL}/eoa/${eoaId}/chat/conversations`, {
    params: { scope },
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
};

// The browser EventSource API can neither POST nor send an Authorization header, so we read the
// SSE stream off a fetch ReadableStream and parse `data:` frames manually. Shared by every
// streaming chat endpoint so the frame parsing lives in one place.
const readSseStream = async (response: Response, onEvent: (event: ChatStreamEvent) => void): Promise<void> => {
  if (!response.body) throw new Error('Response has no body to stream');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const drainBuffer = () => {
    let sepIndex = buffer.indexOf('\n\n');
    while (sepIndex !== -1) {
      const frame = buffer.slice(0, sepIndex);
      buffer = buffer.slice(sepIndex + 2);
      const dataLine = frame.split('\n').find((line) => line.startsWith('data:'));
      if (dataLine) onEvent(JSON.parse(dataLine.slice('data:'.length).trim()) as ChatStreamEvent);
      sepIndex = buffer.indexOf('\n\n');
    }
  };

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    drainBuffer();
  }
};

const postChatStream = async (
  payload: ChatRequest,
  accessToken: string,
  handlers: ChatStreamHandlers = {}
): Promise<ChatResponse> => {
  const response = await fetch(`${BASE_URL}/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
    signal: handlers.signal,
  });

  if (!response.ok || !response.body) {
    throw new Error(`Chat stream request failed with status ${response.status}`);
  }

  let result: ChatResponse | null = null;

  const handleEvent = (event: ChatStreamEvent) => {
    switch (event.type) {
      case 'delta':
        if (event.text) handlers.onDelta?.(event.text);
        break;
      case 'thinking':
        if (event.text) handlers.onThinking?.(event.text);
        break;
      case 'tool_call':
        if (event.tool_name) handlers.onToolCall?.(event.tool_name);
        break;
      case 'tool_result':
        if (event.tool_name) handlers.onToolResult?.(event.tool_name);
        break;
      case 'done':
        result = {
          conversation_id: event.conversation_id ?? '',
          response: event.response ?? '',
          display_messages: event.display_messages ?? [],
        };
        break;
      case 'error':
        throw new Error(event.detail ?? 'Chat stream error');
    }
  };

  await readSseStream(response, handleEvent);

  if (!result) throw new Error('Chat stream ended without a completion event');
  return result;
};

// Generates a PDF summary of the conversation, persists it as a chat turn, and returns the
// updated display messages (the new summary turn is the last one).
const postChatSummary = async (
  payload: ChatSummaryRequest,
  accessToken: string,
  signal?: AbortSignal
): Promise<ChatResponse> => {
  const response = await axios.post<ChatResponse>(`${BASE_URL}/chat/summary`, payload, {
    headers: { Authorization: `Bearer ${accessToken}` },
    signal,
  });
  return response.data;
};

// Resolves a fresh, short-lived presigned URL for a summary PDF. `disposition` controls whether the
// browser opens it inline (View) or downloads it (Download).
const getChatSummaryUrl = async (
  eoaId: string,
  summaryId: string,
  disposition: 'inline' | 'attachment',
  accessToken: string
): Promise<string> => {
  const response = await axios.get<ChatSummaryUrlResponse>(`${BASE_URL}/eoa/${eoaId}/chat/summaries/${summaryId}/url`, {
    params: { disposition },
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data.url;
};

export {
  getChatModels,
  getChatInstructions,
  updateChatInstructions,
  listChatUploads,
  uploadChatUpload,
  deleteChatUpload,
  listConversationUploads,
  uploadConversationUpload,
  deleteConversationUpload,
  listChatConversations,
  getChatConversation,
  postChatStream,
  postChatSummary,
  getChatSummaryUrl,
};
